/**
 * News feed panel — the analyst's primary workspace for scanning events.
 *
 * Features:
 *   - Virtual scrolling for 1000+ items
 *   - Event type filter chips (conflict, cyber, disaster, etc.)
 *   - Source outlet tabs (BBC, Reuters, etc.)
 *   - Source badge on each item
 *   - Click-to-zoom: clicking an item flies the map to its location
 *   - Keyword watchlist highlighting
 *   - Authority tier visual treatment (Tier 1 = amber, Tier 2 = accent border)
 *
 * Sorting: time bucket → authority tier → exact timestamp.
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { getFeedsForVariant, getFeedsForCategories } from '@/config/feeds';
import { getDeskById } from '@/config/desk-presets';
import { getAuthorityTier, getTierLabel } from '@/config/authority';
import type { AuthorityTier } from '@/config/authority';
import type { NormalizedEvent } from '@/sources/base-source';
import { relativeTime } from '@/utils/time';
import { SEVERITY_CSS_COLORS } from '@/utils/severity';
import { filterEventsByTimeRange } from '@/utils/time-filter';
import { applyAriaTabs, updateAriaTabSelection } from '@/utils/aria-tabs';
import { getMap } from '@/map/engine';

/**
 * Topic filter definitions for the news feed.
 * Each filter matches events by: event.type, event.tags, or title keywords.
 * This handles RSS events that have type='custom' but meaningful tags/titles.
 */
interface TopicFilter {
  id: string;
  label: string;
  color: string;
  /** Event types that match this filter. */
  types: Set<string>;
  /** Tag substrings that match (checked against event.tags). */
  tags: string[];
  /** Title keywords that match (case-insensitive). */
  keywords: string[];
}

const TOPIC_FILTERS: TopicFilter[] = [
  {
    id: 'conflict', label: 'Conflict', color: '#ef4444',
    types: new Set(['conflict']),
    tags: ['conflicts', 'conflict', 'war', 'peace-and-security'],
    keywords: ['war', 'airstrike', 'bombing', 'shelling', 'troops', 'offensive', 'ceasefire', 'killed in attack', 'casualties', 'frontline'],
  },
  {
    id: 'military', label: 'Military', color: '#f97316',
    types: new Set(['military']),
    tags: ['military', 'defense', 'defence'],
    keywords: ['military', 'nato', 'missile', 'weapons', 'armed forces', 'naval', 'fighter jet', 'defense', 'defence', 'army', 'navy', 'air force'],
  },
  {
    id: 'protest', label: 'Protest', color: '#eab308',
    types: new Set(['protest', 'social']),
    tags: ['protest', 'unrest', 'social'],
    keywords: ['protest', 'demonstration', 'rally', 'riot', 'unrest', 'uprising', 'march against', 'strike action'],
  },
  {
    id: 'political', label: 'Political', color: '#8b5cf6',
    types: new Set(['political', 'diplomatic']),
    tags: ['political', 'diplomatic', 'politics', 'diplomacy', 'election'],
    keywords: ['election', 'president', 'prime minister', 'parliament', 'sanctions', 'diplomat', 'treaty', 'summit', 'government', 'vote'],
  },
  {
    id: 'cyber', label: 'Cyber', color: '#06b6d4',
    types: new Set(['cyber_incident']),
    tags: ['cyber', 'cybersecurity', 'hacking'],
    keywords: ['cyber', 'hack', 'ransomware', 'malware', 'data breach', 'phishing', 'ddos', 'vulnerability'],
  },
  {
    id: 'disaster', label: 'Disaster', color: '#f43f5e',
    types: new Set(['disaster', 'earthquake', 'fire']),
    tags: ['disaster', 'humanitarian', 'emergency'],
    keywords: ['earthquake', 'tsunami', 'flood', 'hurricane', 'typhoon', 'volcanic', 'landslide', 'wildfire', 'disaster'],
  },
  {
    id: 'economic', label: 'Economic', color: '#10b981',
    types: new Set(['economic']),
    tags: ['economic', 'finance', 'economy', 'trade'],
    keywords: ['economy', 'trade', 'tariff', 'inflation', 'gdp', 'market', 'oil price', 'stock', 'currency', 'bank'],
  },
  {
    id: 'maritime', label: 'Maritime', color: '#0ea5e9',
    types: new Set(['vessel_position']),
    tags: ['maritime', 'shipping'],
    keywords: ['vessel', 'ship', 'maritime', 'port', 'strait', 'piracy', 'cargo', 'tanker', 'naval blockade', 'shadow fleet'],
  },
];

/** Check if an event matches a topic filter. */
function matchesTopic(event: NormalizedEvent, filter: TopicFilter): boolean {
  // Check event type
  if (filter.types.has(event.type)) return true;

  // Check tags
  if (event.tags) {
    for (const tag of event.tags) {
      const lower = tag.toLowerCase();
      for (const filterTag of filter.tags) {
        if (lower.includes(filterTag)) return true;
      }
    }
  }

  // Check title keywords
  const titleLower = event.title.toLowerCase();
  for (const kw of filter.keywords) {
    if (titleLower.includes(kw)) return true;
  }

  return false;
}

/** Source badge color map — deterministic color per source name. */
const SOURCE_COLORS: Record<string, string> = {
  'Reuters': '#ff8c00',
  'BBC': '#bb1919',
  'Al Jazeera': '#d4a017',
  'France 24': '#0055a4',
  'The Guardian': '#052962',
  'CNBC': '#005e9e',
  'UN News': '#4b92db',
  'AP': '#e41e26',
};

function getSourceColor(source: string): string {
  // Check known sources first
  for (const [key, color] of Object.entries(SOURCE_COLORS)) {
    if (source.toLowerCase().startsWith(key.toLowerCase())) return color;
  }
  // Deterministic hash for unknown sources
  let hash = 0;
  for (let i = 0; i < source.length; i++) hash = ((hash << 5) - hash + source.charCodeAt(i)) | 0;
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 50%, 55%)`;
}

/** Get hour-bucket for sorting. Events in the same hour compete on authority tier. */
function getHourBucket(timestamp: string): number {
  return Math.floor(new Date(timestamp).getTime() / 3_600_000);
}

/** Load watchlist keywords from localStorage. */
function loadWatchlist(): string[] {
  try {
    const raw = localStorage.getItem('sentinel-watchlist');
    if (raw) return JSON.parse(raw) as string[];
  } catch { /* ignore */ }
  return [];
}

/** Save watchlist keywords to localStorage. */
function saveWatchlist(keywords: string[]): void {
  try {
    localStorage.setItem('sentinel-watchlist', JSON.stringify(keywords));
  } catch { /* ignore */ }
}

export class NewsFeedPanel {
  private static readonly ITEM_HEIGHT = 72;
  private static readonly OVERSCAN = 8;

  private container: HTMLElement;
  private filterBar: HTMLElement;
  private tabsContainer: HTMLElement;
  private itemsContainer: HTMLElement;
  private virtualInner: HTMLElement;
  private activeSource: string = 'all';
  private activeTopicFilters = new Set<string>();
  private outletNames: string[] = [];
  private outletSourceMap = new Map<string, Set<string>>();
  private renderDebounceId: number | null = null;
  private cleanupFns: (() => void)[] = [];
  private ariaCleanup: (() => void) | null = null;
  private watchlistKeywords: string[] = loadWatchlist();

  private displayData: NormalizedEvent[] = [];
  private displayTierCache = new Map<string, AuthorityTier>();
  private renderedStart = -1;
  private renderedEnd = -1;
  /** When true, next renderItems() resets scroll to top (filter/source change). */
  private scrollResetNeeded = false;

  constructor() {
    this.container = document.createElement('div');
    this.container.className = 'news-feed';
    this.container.style.cssText = 'display: flex; flex-direction: column; height: 100%;';

    // Event type filter chips
    this.filterBar = document.createElement('div');
    this.filterBar.className = 'feed-filter-bar';
    this.buildFilterChips();
    this.container.appendChild(this.filterBar);

    // Source outlet tabs
    this.tabsContainer = document.createElement('div');
    this.tabsContainer.className = 'feed-tabs';
    this.container.appendChild(this.tabsContainer);

    // Scrollable items area
    this.itemsContainer = document.createElement('div');
    this.itemsContainer.className = 'feed-items';
    this.itemsContainer.style.cssText = 'flex: 1; overflow-y: auto;';

    this.virtualInner = document.createElement('div');
    this.virtualInner.className = 'feed-items-inner';
    this.itemsContainer.appendChild(this.virtualInner);

    this.container.appendChild(this.itemsContainer);

    this.handleScroll = this.handleScroll.bind(this);
    this.itemsContainer.addEventListener('scroll', this.handleScroll, { passive: true });

    this.loadSourceTabs(appStore.getState().variant);

    this.cleanupFns.push(
      eventBus.on('variant:changed', ({ to }) => this.loadSourceTabs(to)),
    );

    this.cleanupFns.push(
      appStore.subscribe((s) => s.events, () => this.scheduleRender()),
    );

    this.cleanupFns.push(
      appStore.subscribe((s) => s.timeRange, () => {
        this.scrollResetNeeded = true;
        this.scheduleRender();
      }),
    );
  }

  getElement(): HTMLElement { return this.container; }

  destroy(): void {
    if (this.renderDebounceId !== null) {
      cancelAnimationFrame(this.renderDebounceId);
      this.renderDebounceId = null;
    }
    if (this.ariaCleanup) {
      this.ariaCleanup();
      this.ariaCleanup = null;
    }
    this.itemsContainer.removeEventListener('scroll', this.handleScroll);
    for (const fn of this.cleanupFns) fn();
    this.cleanupFns = [];
    this.displayData = [];
    this.displayTierCache.clear();
  }

  private handleScroll(): void { this.updateVirtualWindow(); }

  private scheduleRender(): void {
    if (this.renderDebounceId !== null) return;
    this.renderDebounceId = requestAnimationFrame(() => {
      this.renderDebounceId = null;
      this.renderItems();
    });
  }

  // ── Filter chips ──

  private buildFilterChips(): void {
    this.filterBar.textContent = '';

    // Watchlist button
    const watchBtn = document.createElement('button');
    watchBtn.className = 'feed-filter-chip feed-watchlist-btn';
    watchBtn.textContent = this.watchlistKeywords.length > 0
      ? `Watchlist (${this.watchlistKeywords.length})`
      : 'Watchlist';
    watchBtn.title = 'Set keyword watchlist — matching events are highlighted';
    watchBtn.onclick = () => this.showWatchlistEditor();
    this.filterBar.appendChild(watchBtn);

    // Divider
    const sep = document.createElement('span');
    sep.style.cssText = 'width: 1px; height: 14px; background: var(--border-primary); flex-shrink: 0;';
    this.filterBar.appendChild(sep);

    // Topic filter chips
    for (const filter of TOPIC_FILTERS) {
      const chip = document.createElement('button');
      chip.className = 'feed-filter-chip';
      if (this.activeTopicFilters.has(filter.id)) chip.classList.add('active');
      chip.dataset.topic = filter.id;

      const dot = document.createElement('span');
      dot.style.cssText = `width: 6px; height: 6px; border-radius: 50%; background: ${filter.color}; flex-shrink: 0;`;
      chip.appendChild(dot);

      const label = document.createElement('span');
      label.textContent = filter.label;
      chip.appendChild(label);

      chip.onclick = () => {
        if (this.activeTopicFilters.has(filter.id)) {
          this.activeTopicFilters.delete(filter.id);
          chip.classList.remove('active');
        } else {
          this.activeTopicFilters.add(filter.id);
          chip.classList.add('active');
        }
        this.scrollResetNeeded = true;
        this.renderItems();
      };

      this.filterBar.appendChild(chip);
    }
  }

  private showWatchlistEditor(): void {
    const existing = this.container.querySelector('.feed-watchlist-editor');
    if (existing) { existing.remove(); return; }

    const editor = document.createElement('div');
    editor.className = 'feed-watchlist-editor';
    editor.style.cssText = `
      padding: 8px 10px; background: var(--bg-secondary);
      border-bottom: 1px solid var(--border-primary);
      display: flex; gap: 6px; align-items: center;
    `;

    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Keywords (comma-separated): Gotland, Wagner, shadow fleet';
    input.value = this.watchlistKeywords.join(', ');
    input.style.cssText = `
      flex: 1; background: var(--bg-primary); border: 1px solid var(--border-primary);
      border-radius: 4px; padding: 4px 8px; color: var(--text-primary);
      font-size: 11px; font-family: var(--font-ui); outline: none;
    `;

    const saveBtn = document.createElement('button');
    saveBtn.textContent = 'Save';
    saveBtn.style.cssText = `
      padding: 4px 10px; font-size: 10px; font-weight: 600;
      background: #00b4d8; color: #000; border: none; border-radius: 4px;
      cursor: pointer; font-family: var(--font-ui);
    `;
    saveBtn.onclick = () => {
      this.watchlistKeywords = input.value
        .split(',')
        .map(k => k.trim().toLowerCase())
        .filter(k => k.length > 0);
      saveWatchlist(this.watchlistKeywords);
      editor.remove();
      this.buildFilterChips();
      this.scrollResetNeeded = true;
      this.scheduleRender();
    };

    editor.appendChild(input);
    editor.appendChild(saveBtn);

    // Insert after filter bar
    this.filterBar.after(editor);
    input.focus();
  }

  // ── Source tabs ──

  private loadSourceTabs(deskId: string): void {
    const desk = getDeskById(deskId);
    const feeds = desk ? getFeedsForCategories(desk.feedCategories) : getFeedsForVariant(deskId);

    this.outletSourceMap.clear();
    const outletOrder: string[] = [];

    for (const feed of feeds) {
      const outlet = getOutletName(feed.name);
      if (!this.outletSourceMap.has(outlet)) {
        this.outletSourceMap.set(outlet, new Set());
        outletOrder.push(outlet);
      }
      this.outletSourceMap.get(outlet)!.add(feed.name);
    }

    this.outletNames = outletOrder;
    this.activeSource = 'all';
    this.renderTabs();
    this.scrollResetNeeded = true;
    this.renderItems();
  }

  private renderTabs(): void {
    if (this.ariaCleanup) {
      this.ariaCleanup();
      this.ariaCleanup = null;
    }

    this.tabsContainer.textContent = '';

    const allTab = this.createTab('ALL', 'all');
    allTab.classList.add('active');
    this.tabsContainer.appendChild(allTab);

    for (const outlet of this.outletNames) {
      const tab = this.createTab(outlet.toUpperCase(), outlet);
      this.tabsContainer.appendChild(tab);
    }

    this.ariaCleanup = applyAriaTabs({
      tabList: this.tabsContainer,
      tabSelector: '.feed-tab',
      panel: this.itemsContainer,
      prefix: 'feed',
    });
  }

  private createTab(label: string, sourceId: string): HTMLButtonElement {
    const tab = document.createElement('button');
    tab.className = 'feed-tab';
    tab.textContent = label;
    tab.dataset.source = sourceId;

    tab.onclick = () => {
      this.activeSource = sourceId;
      const tabs = this.tabsContainer.querySelectorAll('.feed-tab');
      tabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      updateAriaTabSelection(this.tabsContainer, '.feed-tab', this.itemsContainer);
      this.scrollResetNeeded = true;
      this.renderItems();
    };

    return tab;
  }

  // ── Data pipeline ──

  private static readonly FEED_EXCLUDED_SOURCES = new Set([
    'gdelt', 'firms', 'usgs', 'gdacs', 'gpsjam', 'eonet',
    'opensky', 'vesselfinder', 'ais-ws',
    'military-bases', 'sanctions',
    'airport-delays', 'cable-health', 'outages',
    'weather', 'oref', 'advisory', 'cyber',
    'fred', 'polymarket', 'displacement', 'ucdp',
  ]);

  private renderItems(): void {
    const state = appStore.getState();
    const events = filterEventsByTimeRange(state.events, state.timeRange);

    let feedEvents = events.filter(e => !NewsFeedPanel.FEED_EXCLUDED_SOURCES.has(e.source));

    // Apply source filter
    if (this.activeSource !== 'all') {
      const sourceNames = this.outletSourceMap.get(this.activeSource);
      if (sourceNames) {
        feedEvents = feedEvents.filter(e => sourceNames.has(e.source));
      } else {
        feedEvents = feedEvents.filter(e => e.source === this.activeSource);
      }
    }

    // Apply topic filters — event must match at least one active filter
    if (this.activeTopicFilters.size > 0) {
      const activeFilters = TOPIC_FILTERS.filter(f => this.activeTopicFilters.has(f.id));
      feedEvents = feedEvents.filter(e => activeFilters.some(f => matchesTopic(e, f)));
    }

    // Hide untranslated foreign-language content
    feedEvents = feedEvents.filter(e => {
      // Allow English content and content without language detection
      if (!e.language || e.language === 'en' || e.language === 'unknown') return true;
      // Allow successfully translated content
      if (e.tags?.includes('translated')) return true;
      // Hide untranslated foreign content
      return false;
    });

    // Reject pipeline artifacts: titles that are raw JSON or empty
    feedEvents = feedEvents.filter(e => {
      const title = e.title?.trim();
      return title && !title.startsWith('{') && !title.startsWith('[');
    });

    // Pre-compute authority tiers
    const tierCache = new Map<string, AuthorityTier>();
    for (const e of feedEvents) {
      if (!tierCache.has(e.source)) {
        tierCache.set(e.source, getAuthorityTier(e.source));
      }
    }

    // Sort: time bucket → authority tier → exact timestamp
    const sorted = feedEvents.slice().sort((a, b) => {
      const bucketA = getHourBucket(a.timestamp);
      const bucketB = getHourBucket(b.timestamp);
      if (bucketA !== bucketB) return bucketB - bucketA;

      const tierA = tierCache.get(a.source) ?? 4;
      const tierB = tierCache.get(b.source) ?? 4;
      if (tierA !== tierB) return tierA - tierB;

      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });

    // Deduplicate by normalized title
    const seen = new Set<string>();
    const deduped: NormalizedEvent[] = [];
    for (const event of sorted) {
      const key = event.title.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(event);
      }
    }

    this.displayData = deduped;
    this.displayTierCache = tierCache;
    this.renderedStart = -1;
    this.renderedEnd = -1;
    // Only reset scroll on user-initiated changes (filter/source/watchlist),
    // not on background data updates — prevents snapping to top while reading
    if (this.scrollResetNeeded) {
      this.itemsContainer.scrollTop = 0;
      this.scrollResetNeeded = false;
    }
    this.updateVirtualWindow();
  }

  // ── Virtual scroll ──

  private updateVirtualWindow(): void {
    const data = this.displayData;

    if (data.length === 0) {
      this.virtualInner.textContent = '';
      this.virtualInner.style.paddingTop = '0';
      this.virtualInner.style.paddingBottom = '0';

      const empty = document.createElement('div');
      empty.style.cssText = 'padding: 32px 16px; text-align: center; color: var(--text-muted); font-size: 12px;';
      empty.textContent = this.activeTopicFilters.size > 0
        ? 'No events match the active filters.'
        : 'No articles yet. Feeds are loading\u2026';
      this.virtualInner.appendChild(empty);
      return;
    }

    const scrollTop = this.itemsContainer.scrollTop;
    const containerHeight = this.itemsContainer.clientHeight;
    const itemHeight = NewsFeedPanel.ITEM_HEIGHT;
    const overscan = NewsFeedPanel.OVERSCAN;
    const totalItems = data.length;

    const firstVisible = Math.floor(scrollTop / itemHeight);
    const visibleCount = Math.ceil(containerHeight / itemHeight);
    const startIndex = Math.max(0, firstVisible - overscan);
    const endIndex = Math.min(totalItems, firstVisible + visibleCount + overscan);

    if (startIndex === this.renderedStart && endIndex === this.renderedEnd) return;
    this.renderedStart = startIndex;
    this.renderedEnd = endIndex;

    this.virtualInner.style.paddingTop = `${startIndex * itemHeight}px`;
    this.virtualInner.style.paddingBottom = `${Math.max(0, (totalItems - endIndex) * itemHeight)}px`;

    this.virtualInner.textContent = '';
    for (let i = startIndex; i < endIndex; i++) {
      const event = data[i];
      const tier = this.displayTierCache.get(event.source) ?? 4;
      this.virtualInner.appendChild(this.createFeedItem(event, tier));
    }
  }

  // ── Feed item rendering ──

  /** Check if an event title matches any watchlist keywords. */
  private matchesWatchlist(title: string): boolean {
    if (this.watchlistKeywords.length === 0) return false;
    const lower = title.toLowerCase();
    return this.watchlistKeywords.some(kw => lower.includes(kw));
  }

  private createFeedItem(event: NormalizedEvent, tier: AuthorityTier): HTMLElement {
    const item = document.createElement('div');
    item.className = 'feed-item';
    item.setAttribute('role', 'button');
    item.setAttribute('tabindex', '0');

    const isWatchlistMatch = this.matchesWatchlist(event.title);

    // Watchlist highlight — gold left border
    if (isWatchlistMatch) {
      item.style.borderLeft = '3px solid #eab308';
      item.style.background = 'rgba(234, 179, 8, 0.06)';
    } else if (tier === 2) {
      item.style.borderLeft = '2px solid var(--text-muted)';
    }

    // Header row
    const header = document.createElement('div');
    header.className = 'feed-item-header';

    // Severity dot
    const severityDot = document.createElement('div');
    severityDot.className = 'feed-item-severity';
    const severity = event.severity ?? 'info';
    severityDot.style.background = SEVERITY_CSS_COLORS[severity] ?? SEVERITY_CSS_COLORS['info'];
    header.appendChild(severityDot);

    // Topic badge — show the first matching topic filter
    const matchedTopic = TOPIC_FILTERS.find(f => matchesTopic(event, f));
    if (matchedTopic) {
      const typeBadge = document.createElement('span');
      typeBadge.className = 'feed-item-badge type-badge';
      typeBadge.style.cssText = `background: ${matchedTopic.color}22; color: ${matchedTopic.color}; border: 1px solid ${matchedTopic.color}44;`;
      typeBadge.textContent = matchedTopic.label.toUpperCase();
      header.appendChild(typeBadge);
    }

    // Breaking badge
    if (severity === 'critical') {
      const badge = document.createElement('span');
      badge.className = 'feed-item-badge breaking';
      badge.textContent = 'BREAKING';
      header.appendChild(badge);
    }

    // Watchlist match indicator
    if (isWatchlistMatch) {
      const watchBadge = document.createElement('span');
      watchBadge.className = 'feed-item-badge';
      watchBadge.style.cssText = 'background: rgba(234, 179, 8, 0.15); color: #eab308; border: 1px solid rgba(234, 179, 8, 0.3);';
      watchBadge.textContent = 'WATCHLIST';
      header.appendChild(watchBadge);
    }

    // Location indicator (if event has coordinates)
    if (event.location) {
      const locBadge = document.createElement('span');
      locBadge.className = 'feed-item-loc';
      locBadge.title = event.location.name
        ? `${event.location.name} (${event.location.lat.toFixed(2)}, ${event.location.lng.toFixed(2)})`
        : `${event.location.lat.toFixed(2)}, ${event.location.lng.toFixed(2)}`;
      locBadge.style.cssText = 'margin-left: auto; font-size: 9px; color: var(--text-muted); cursor: pointer;';
      locBadge.textContent = event.location.name || `${event.location.lat.toFixed(1)}°, ${event.location.lng.toFixed(1)}°`;
      locBadge.onclick = (e) => {
        e.stopPropagation();
        this.flyToEvent(event);
      };
      header.appendChild(locBadge);
    }

    item.appendChild(header);

    // Title
    const title = document.createElement('div');
    title.className = 'feed-item-title';
    title.textContent = event.title;
    item.appendChild(title);

    // Meta row: source badge + time + investigate
    const meta = document.createElement('div');
    meta.className = 'feed-item-meta';

    // Source badge (colored dot + name)
    const srcColor = getSourceColor(event.source);
    const sourceBadge = document.createElement('span');
    sourceBadge.className = 'feed-item-source';
    sourceBadge.innerHTML = `<span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:${srcColor};margin-right:4px;vertical-align:middle;"></span>${escapeHtml(event.source)}`;
    applyTierStyle(sourceBadge, tier);
    sourceBadge.title = getTierLabel(tier);
    meta.appendChild(sourceBadge);

    const dot = document.createElement('span');
    dot.textContent = '\u00B7';
    meta.appendChild(dot);

    const time = document.createElement('span');
    time.textContent = relativeTime(event.timestamp);
    meta.appendChild(time);

    // Investigate link
    const invLink = document.createElement('span');
    invLink.className = 'feed-item-investigate';
    invLink.style.cssText = 'font-size: 8px; color: #00b4d8; cursor: pointer; margin-left: 6px; font-weight: 600;';
    invLink.textContent = 'Investigate';
    invLink.addEventListener('click', (e) => {
      e.stopPropagation();
      eventBus.emit('investigation:request', { query: event.title, tier: 'STANDARD' });
    });
    meta.appendChild(invLink);

    item.appendChild(meta);

    // Click handler — select event + fly to location
    const activate = () => {
      eventBus.emit('event:selected', event);
      this.flyToEvent(event);
    };
    item.onclick = activate;
    item.onkeydown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        activate();
      }
    };

    return item;
  }

  /** Fly the map to an event's location. */
  private flyToEvent(event: NormalizedEvent): void {
    if (!event.location) return;
    const map = getMap();
    if (!map) return;
    map.flyTo({
      center: [event.location.lng, event.location.lat],
      zoom: Math.max(map.getZoom(), 6),
      duration: 1200,
    });
  }
}

// ── Helpers ──

function applyTierStyle(el: HTMLElement, tier: AuthorityTier): void {
  switch (tier) {
    case 1:
      el.style.color = 'var(--accent-amber)';
      el.style.fontWeight = '600';
      break;
    case 2:
      el.style.color = 'var(--text-secondary)';
      break;
  }
}

function getOutletName(feedName: string): string {
  const lower = feedName.toLowerCase();
  if (lower.startsWith('bbc')) return 'BBC';
  if (lower.startsWith('al jazeera')) return 'Al Jazeera';
  if (lower.startsWith('france 24')) return 'France 24';
  if (lower.startsWith('un news')) return 'UN News';
  if (lower.startsWith('the guardian')) return 'The Guardian';
  if (lower.startsWith('cnbc')) return 'CNBC';
  if (lower.startsWith('reuters')) return 'Reuters';
  return feedName;
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Factory function to create the news feed panel element. */
export function createNewsFeedPanel(): HTMLElement {
  const panel = new NewsFeedPanel();
  const el = panel.getElement();
  (el as HTMLElement & { destroy?: () => void }).destroy = () => panel.destroy();
  return el;
}
